import Box from "@mui/material/Box";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams,} from "ag-grid-community";
import {AgGridReact} from "ag-grid-react";
import * as React from "react";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import SectionHeader from "../../../../util/component/SectionHeader";
import {owLocationGroupAtom} from "../../state/owLocationGroupsRecoil";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {fromGenericBeToGenericFe, getMobilityStores, onGridReady, useActionColDef, resolveFilterCodes} from "../../utils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import {
    DialogInterface,
    FormDialog,
    GenericComponentModel,
    SelectElementModelBE,
    SelectElementModelFE,
} from "../../api/types/generic";
import {locationAtom} from "../../state/locationsRecoil";
import {Location} from "../../api/types/location";
import * as yup from "yup";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {Button, Dialog, DialogActions, DialogContent, Grid, IconButton, Tooltip, useTheme} from "@mui/material";
import Loading from "../../common/components/Loading";
import SearchFilters from "../../common/components/SearchFilters";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import AddIcon from "@mui/icons-material/Add";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import BasicToolbar from "../../common/components/BasicToolbar";
import DialogComponent from "../../common/components/DialogComponent";
import {useMutation, useQuery} from "@tanstack/react-query";
import {Delete} from "@mui/icons-material";
import {useOWLocationGroupRepository} from "../../api/repositories/OWLocationGroupRepository";
import {useOWLocationGroupLocationRepository} from "../../api/repositories/OWLocationGroupLocationRepository";
import {selectedOWLocationGroupLocationAtom,} from "../../state/owLocationGroupLocationsRecoil";
import {
    FormOWLocationGroupLocationModel,
    FormSearchOWLocationGroupLocationModel,
    LocatonGroupLocationReplacementRequest,
    OWLocationGroupLocations,
} from "../../api/types/owLocationGroupLocation";
import FormOWLocationGroupLocation from "./FormOWLocationGroupLocation";
import FormOWLocationGroup from "./FormOWLocationGroup";
import {
    FormOWLocationGroupModel,
    OWLocationGroupEntityRequest,
    OWLocationGroupEntityResponse,
} from "../../api/types/owLocationGroup";
import UpdateActionIcon from "../../common/components/UpdateActionIcon";
import {useLocationRepository} from "../../api/repositories/LocationRepository";

const OWLocationGroupLocation = () => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Ref
    const owLocationGroupGridRef = useRef<AgGridReact>(null);
    const locationGridRef = useRef<AgGridReact>(null);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Recoil
    const [, setOWLocationGroups] = useRecoilState(owLocationGroupAtom);
    const [, setLocations] = useRecoilState(locationAtom);
    const [selectedOWLocationGroupLocation, setSelectedOWLocationGroupLocation] = useRecoilState(selectedOWLocationGroupLocationAtom);

    // Use Repository Hooks
    const {
        putOWLocationGroupLocation,
        getOWLocationGroupLocationsPaged
    } = useOWLocationGroupLocationRepository();
    const {
        getActiveOWLocationGroups,
        postOWLocationGroup,
        putOWLocationGroup,
        deleteOWLocationGroup
    } = useOWLocationGroupRepository();
    const {getLocationAll, postLocationAll, putLocationAll} = useLocationRepository();

    // Use Mutation
    const mutationPostOWLocationGroup = useMutation({
        mutationFn: (owLocationGroupEntityRequest: OWLocationGroupEntityRequest) => postOWLocationGroup(owLocationGroupEntityRequest),
        onSuccess: () => {
            // reset form
            setOWLocationGroupFormDialog(null);
            owLocationGroupForm.reset({name: ""});
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            handleGetOWLocationGroupLocationsPage();
            handleGetStaticContent();
        }
    });
    const mutationPutOWLocationGroup = useMutation({
        mutationFn: (owLocationGroupEntityResponse: OWLocationGroupEntityResponse) => putOWLocationGroup(owLocationGroupEntityResponse),
        onSuccess: () => {
            // reset form
            setOWLocationGroupFormDialog(null);
            owLocationGroupForm.reset({name: ""});
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            handleGetOWLocationGroupLocationsPage();
            handleGetStaticContent();
        }
    });

    // Use State
    const [owLocationGroupLocationFormDialog, setOWLocationGroupLocationFormDialog] = useState<FormDialog | null>(null);
    const [owLocationGroupFormDialog, setOWLocationGroupFormDialog] = useState<FormDialog | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    // Use Query
    const locationsQuery = useQuery({
        queryKey: ["pricing_location_query"],
        queryFn: getLocationAll,
    });

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        }).map((el) => el.code) as string[];

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");


    const [testFilters, setTestFilters] = useState<FormSearchOWLocationGroupLocationModel>({
        owLocationGroupName: "",
        locationName: "",
        legalEntities: everyLegalEntitiesWithoutAll
    })

    const handleGetStaticContent = async () => {
        try {
            if (locationsQuery?.data) {
                await getMobilityStores(
                    locationsQuery?.data as Location[],
                    user,
                    postLocationAll,
                    putLocationAll
                );
            }

            await handleGetOWLocationGroupLocationsPage();
            const owLocationGroups = await getActiveOWLocationGroups(optionsFiltered);
            setOWLocationGroups(
                fromGenericBeToGenericFe(owLocationGroups).sort((a, b) =>
                    a.label.localeCompare(b.label)
                )
            );
        } catch (e) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
        }
    };

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
        // eslint-disable-next-line
    }, [locationsQuery?.data]);

    const handleGetOWLocationGroupLocationsPage = () => {
        owLocationGroupGridRef?.current?.api.refreshServerSide({purge: true});
    };

    const handleTriggerFilters = (entries: FormSearchOWLocationGroupLocationModel) => {
        const hardcodedFilter = Object.entries(entries).reduce((acc, [key, value]) => ({
            ...acc,
            [key]: {type: "contains", filter: value}
        }), {});

        owLocationGroupGridRef.current?.api.paginationGoToFirstPage();
        owLocationGroupGridRef.current?.api.setFilterModel(hardcodedFilter);
        owLocationGroupGridRef.current?.api.onFilterChanged();
        if (selectedOWLocationGroupLocation)
            setSelectedOWLocationGroupLocation(null);
        owLocationGroupGridRef?.current?.api?.deselectAll();
    };

    useEffect(() => {
        if (owLocationGroupGridRef?.current) {
            handleTriggerFilters({
                owLocationGroupName: testFilters.owLocationGroupName,
                locationName: testFilters.locationName,
                legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
            });
        }
    }, []);

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        // LPP-24637: legalEntity e' obbligatoria per il servizio di pricing.
        // Il reset deve ripristinare i valori del profilo utente (come lo stato iniziale),
        // non svuotarli: con la lista vuota la ricerca viene rifiutata con 400 "Validation failure".
        setTestFilters({
            owLocationGroupName: "",
            locationName: "",
            legalEntities: everyLegalEntitiesWithoutAll
        });
        handleTriggerFilters({
            owLocationGroupName: "",
            locationName: "",
            legalEntities: everyLegalEntitiesWithoutAll
        });
    };

    const handleTriggerFiltersInitial = () => {
        const everyLegalEntitiesWithoutAll = legalEntityOptions
            .map((el) => el.label)
            .filter((el) => el != "all");
        handleTriggerFilters({
            owLocationGroupName: testFilters.owLocationGroupName,
            locationName: testFilters.locationName,
            legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const handleConfirmSaveOWLocationGroupLocation = async (formValues: FormOWLocationGroupLocationModel) => {
        try {
            const owLocationGroupLocationsIds: LocatonGroupLocationReplacementRequest[] = [{
                oldAssociation: {
                    owLocationGroupId: selectedOWLocationGroupLocation?.owLocationGroup.id as number,
                    locationId: formValues.location?.code as string
                },
                newAssociation: {
                    owLocationGroupId: formValues.owLocationGroup?.code as number,
                    locationId: formValues.location?.code as string
                }
            }];
            await putOWLocationGroupLocation(owLocationGroupLocationsIds);
            handleGetOWLocationGroupLocationsPage();
            setLocations([]);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            setOWLocationGroupLocationFormDialog(null);
            owLocationGroupLocationForm.reset();

            if (
                (selectedOWLocationGroupLocation?.owLocationGroup?.id as number) !==
                (formValues?.owLocationGroup?.code as number)
            ) {
                owLocationGroupGridRef?.current?.api?.deselectAll();
                setSelectedOWLocationGroupLocation(null);
            }
        } catch (e) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
        }
    };

    // FORM LOCATION GROUP - START //
    const validationSchemaLocationGroup = yup
        .object()
        .shape({
            name: yup
                .string()
                .trim()
                .lowercase()
                .required(t("translation:validation.fieldMandatory"))
                .min(2),
            legalEntity: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
        })
        .defined();

    // Use Form
    const owLocationGroupForm = useForm<FormOWLocationGroupModel>({
        defaultValues: {name: "", legalEntities: null},
        resolver: yupResolver(
            validationSchemaLocationGroup,
            {},
            {
                rawValues: true,
            }
        ),
        shouldUnregister: true,
    });
    // FORM LOCATION GROUP - END //

    // FORM LOCATION GROUP LOCATION - START //
    const validationSchemaLocationGroupLocation = yup
        .object()
        .shape({
            owLocationGroup: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            location: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            legalEntities: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
        })
        .defined();

    // Use Form
    const owLocationGroupLocationForm = useForm<FormOWLocationGroupLocationModel>(
        {
            defaultValues: {
                owLocationGroup: null,
                location: null,
                legalEntities: [],
            },
            resolver: yupResolver(
                validationSchemaLocationGroupLocation,
                {},
                {
                    rawValues: true,
                }
            ),
            shouldUnregister: true,
        }
    );
    // FORM LOCATION GROUP LOCATION - END //

    // ************ AG GRID START ************ //
    // Use Mutation
    const mutationDeleteOWLocationGroupLocation = useMutation({
        mutationFn: (id: number) => deleteOWLocationGroup(id || 0),
        onSuccess: async () => {
            handleGetOWLocationGroupLocationsPage();
            const owLocationGroups = await getActiveOWLocationGroups(optionsFiltered);
            setOWLocationGroups(
                fromGenericBeToGenericFe(owLocationGroups).sort((a, b) =>
                    a.label.localeCompare(b.label)
                )
            );
        },
    });

    const handleConfirmDelete = async (deletingRowIndex: number) => {
        try {
            await mutationDeleteOWLocationGroupLocation.mutate(deletingRowIndex);
            setDialog(null);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const deleteButtonClickedLocationGroup = (
        params: ICellRendererParams<OWLocationGroupLocations>
    ) => {
        setDialog({
            open: true,
            message: t(
                "pricing:owLocations.dialogContents.removeOWLocationGroupLocation"
            ),
            onClickConfirm: () =>
                handleConfirmDelete(params.data?.owLocationGroup.id as number),
        });
    };

    const updateButtonClickedLocationGroup = (
        params: ICellRendererParams<Location>
    ) => {
        handleClickPutLocationGroupLocation(params.data as Location);
    };

    const handleClickPutLocationGroupLocation = (location: Location) => {
        if (location) {
            const result = {
                location: {
                    code: location.id,
                    label: location.name,
                },
            };
            owLocationGroupLocationForm.reset(result); // asynchronously reset your form values
            setOWLocationGroupLocationFormDialog({
                open: true,
                onClickConfirm: owLocationGroupLocationForm.handleSubmit(
                    handleConfirmSaveOWLocationGroupLocation
                ),
                readonly: false,
            });
        }
    };

    const {actionColDef} = useActionColDef();

    const owLocationGroupColumnDefs: ColDef[] = [
        {
            headerName: t("pricing:owLocations.table.headers.owLocationGroup"),
            headerTooltip: t("pricing:owLocations.table.headers.owLocationGroup"),
            field: "owLocationGroupName",
            editable: false,
            cellRenderer: (params: ICellRendererParams<OWLocationGroupLocations>) =>
                params.data?.owLocationGroup.name,
            flex: 1,
            filter: "text",
        },
        {
            field: "locationName",
            hide: true,
            filter: "text",
        },
        {
            headerName: t("pricing:owLocations.table.headers.legalEntities"),
            headerTooltip: t("pricing:locations.table.headers.legalEntities"),
            field: "legalEntities",
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.owLocationGroup.legalEntity)),
            hide: user.legalEntities.length != 1 ? false : true
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams<OWLocationGroupLocations>) =>
                params.data?.owLocationGroup.name !== "STD" &&
                params.data?.owLocationGroup.active &&
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_DELETE_OW_LOCATION_GROUP_LOCATION";
                    });
                }) && (
                    <Tooltip
                        arrow
                        title={t("pricing:tooltips.delete")}
                        placement={"right-start"}
                    >
                        <IconButton
                            aria-label="close"
                            onClick={() => deleteButtonClickedLocationGroup(params)}
                            sx={{
                                margin: "auto",
                                color: theme.palette.accent.main,
                            }}
                        >
                            <Delete/>
                        </IconButton>
                    </Tooltip>
                ),
        },
    ];

    const locationColumnDefs: ColDef[] = [
        {
            headerName: t("pricing:locations.table.headers.location"),
            headerTooltip: t("pricing:locations.table.headers.location"),
            field: "name",
            flex: 1
        },
        {
            headerName: t("pricing:locations.table.headers.region"),
            headerTooltip: t("pricing:locations.table.headers.region"),
            field: "region",
            flex: 1
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams<Location>) => (
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_UPDATE_OW_LOCATION_GROUP_LOCATION";
                    });
                }) &&
                <UpdateActionIcon params={params}
                                  action={() => updateButtonClickedLocationGroup(params)}
                                  styles={{
                                      margin: "auto",
                                      color: theme.palette.accent.main
                                  }}
                />
            )
        }
    ];

    const defaultColDef = DEFAULT_COL_DEF;

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].sort;

                const {filterModel} = params.request;

                const filter: FormSearchOWLocationGroupLocationModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce((acc, [key, value]) => ({
                    ...acc,
                    [key]: value?.filter || ""
                }), testFilters);
                sort = sort === "owLocationGroupName" ? "owLocationGroup_name" : sort;

                const owLocationGroupLocationsPaged =
                    await getOWLocationGroupLocationsPaged({
                        ...filter,
                        sort: sort && order ? `${sort},${order}` : "",
                        size:
                            owLocationGroupGridRef?.current?.api?.paginationGetPageSize() ||
                            10,
                        page:
                            owLocationGroupGridRef?.current?.api?.paginationGetCurrentPage() ||
                            0,
                    });
                if (owLocationGroupLocationsPaged.totalElements === 0) {
                    owLocationGroupGridRef.current!.api.showNoRowsOverlay();
                } else {
                    owLocationGroupGridRef.current!.api.hideOverlay();
                }

                params.success({
                    rowData: owLocationGroupLocationsPaged.content,
                    rowCount: owLocationGroupLocationsPaged.totalElements,
                });

                const selectedData: OWLocationGroupLocations[] =
                    owLocationGroupGridRef?.current?.api?.getSelectedRows() || [];
                if (selectedData?.length === 0) {
                    locationGridRef?.current?.api?.setRowData([]);
                    locationGridRef?.current?.api?.refreshCells();
                } else {
                    const owLocationGroupLocations: OWLocationGroupLocations =
                        selectedData[0] as OWLocationGroupLocations;
                    const locations: Location[] =
                        owLocationGroupLocations?.locations || [];
                    locationGridRef?.current?.api?.setRowData(locations);
                    locationGridRef?.current?.api?.refreshCells();
                }
            },
        };
    };
    // ************ AG GRID END ************ //

    const onGroupSelectionChange = useCallback(() => {
        const selectedRows: any[] =
            owLocationGroupGridRef.current!.api.getSelectedRows();
        const owLocationGroupLocations: OWLocationGroupLocations =
            selectedRows[0] as OWLocationGroupLocations;
        const locations: Location[] = owLocationGroupLocations?.locations || [];
        setSelectedOWLocationGroupLocation(owLocationGroupLocations);
        locationGridRef?.current?.api?.setRowData(locations);
        locationGridRef?.current?.api?.refreshCells();
    }, []);

    const filters: GenericComponentModel[] = [
        {
            name: "owLocationGroupName",
            value: testFilters.owLocationGroupName as string,
            label: `${t("pricing:owLocations.form.labels.owLocationGroup")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    owLocationGroupName: value,
                }),
            options: [],
            type: "TextField",
        },
        {
            name: "locationName",
            value: testFilters.locationName as string,
            label: `${t("pricing:owLocations.form.labels.location")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    locationName: value,
                }),
            options: [],
            type: "TextField",
        },
        {
            name: "legalEntity",
            value:
                testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:locations.form.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length != 1 ? false : true
        },
    ];

    const handleConfirmSave = async (values: FormOWLocationGroupModel) => {
        const newOWLocationGroup: OWLocationGroupEntityRequest = {
            name: values.name,
            active: true,
            legalEntity: legalEntityOptions.length === 1 ? everyLegalEntitiesWithoutAll[0] : values.legalEntities?.code as string,
        };

        try {
            values?.id
                ? await mutationPutOWLocationGroup.mutate({
                    id: values?.id,
                    ...JSON.parse(JSON.stringify(newOWLocationGroup)),
                })
                : await mutationPostOWLocationGroup.mutate(newOWLocationGroup);
        } catch (e) {
        }
    };

    const toolbarLocationGroup = {
        renderActions: () => (
            <BasicToolbar addIcon={
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_CREATE_OW_LOCATION_GROUP_LOCATION";
                    });
                }) ?
                    {
                        onClick: () => setOWLocationGroupFormDialog({
                            open: true,
                            onClickConfirm: owLocationGroupForm.handleSubmit(handleConfirmSave),
                            readonly: false
                        })
                    } : undefined}/>
        )
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_OW_LOCATION_GROUP_LOCATION.name")}/>
            {owLocationGroupLocationFormDialog?.open &&
                <Dialog open={owLocationGroupLocationFormDialog.open} maxWidth={"lg"} fullWidth>
                    <BootstrapDialogTitle id="customized-dialog-title" onClose={() => {
                        owLocationGroupLocationForm.reset();
                        setOWLocationGroupLocationFormDialog(null);
                    }}>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:owLocations.section.titles.addLocationToOWLocationGroup")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main}}>
                        <FormProvider {...owLocationGroupLocationForm}>
                            <FormOWLocationGroupLocation/>
                        </FormProvider>
                    </DialogContent>
                    <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                        <Button variant="outlined"
                                color={"accent"}
                                onClick={() => {
                                    owLocationGroupLocationForm.reset();
                                    setOWLocationGroupLocationFormDialog(null);
                                }}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button sx={{marginLeft: "16px"}}
                                autoFocus
                                color={"accent"}
                                onClick={owLocationGroupLocationFormDialog?.onClickConfirm}
                        >
                            {t("pricing:actions.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            }

            {owLocationGroupFormDialog?.open &&
                <Dialog open={owLocationGroupFormDialog.open} maxWidth={"lg"} fullWidth>
                    <BootstrapDialogTitle id="customized-dialog-title" onClose={() => {
                        owLocationGroupForm.reset({name: ""});
                        setOWLocationGroupFormDialog(null);
                    }}>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:owLocations.section.titles.owLocationGroup")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main}}>
                        <FormProvider {...owLocationGroupForm}>
                            <FormOWLocationGroup readOnly={false}/>
                        </FormProvider>
                    </DialogContent>
                    {!owLocationGroupFormDialog.readonly &&
                        <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                            <Button variant="outlined"
                                    color={"accent"}
                                    onClick={() => {
                                        setOWLocationGroupFormDialog(null);
                                        owLocationGroupForm.reset({name: ""});
                                    }}
                            >
                                {t("pricing:actions.cancel")}
                            </Button>
                            <Button sx={{marginLeft: "16px"}}
                                    autoFocus
                                    color={"accent"}
                                    onClick={owLocationGroupFormDialog?.onClickConfirm}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>
                        </DialogActions>}
                </Dialog>
            }

            {/* <CardContainer> */}
            <SearchFilters
                filtersOutOfCollapse={filters}
                filtersInCollapse={[]}
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
            />
            {/* </CardContainer> */}

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} md={6}>
                    <AgGridCustomTable
                        gridRef={owLocationGroupGridRef}
                        columnDefs={owLocationGroupColumnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbarLocationGroup}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onSelectionChanged={onGroupSelectionChange}
                        rowSelection="single"
                        onGridReady={() => onGridReady({
                            initialSortColId: "owLocationGroupName",
                            gridRef: owLocationGroupGridRef,
                            createDatasource
                        })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <AgGridCustomTable
                        gridRef={locationGridRef}
                        rowData={selectedOWLocationGroupLocation?.locations || []}
                        columnDefs={locationColumnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={{
                            renderActions: () => (
                                <div style={{overflow: "hidden"}}>
                                    <AddIcon sx={{color: "transparent", overflow: "hidden"}}/>
                                </div>)
                        }}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        paginationPageSize={10}
                    />
                </Grid>
            </Grid>

            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => {
                        setDialog(null);
                    }}
                    open={dialog.open}
                    message={dialog.message}
                />
            }

            <Loading/>
        </Box>
    );
};

export default OWLocationGroupLocation;
